import {
  Component,
  DoCheck,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  input,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import {
  MetabaseService,
  EmbeddingParams,
  DashboardParams,
} from '../../services/metabase.service';
import { UserProfileService } from '../../services/user/user-profile.service';
import { ThemeService } from '../../services/theme.service';
import { environment } from '../../../environments/environment';

/** After this long without the frame loading, offer a retry. */
const SLOW_MS = 20_000;

/** The frame could not be set up at all (an unexpected error). */
const PREPARE_FAILED = 'Something went wrong while opening the dashboard. Try again, and contact support if it keeps happening.';
/** The embed link could not be created (signing failed). */
const LINK_FAILED = 'A secure link to the dashboard could not be created. Try again, and contact support if it keeps happening.';

/**
 * A Metabase dashboard, embedded headerless so the console page owns the
 * title. It signs the embed token for the signed-in organisation, follows
 * the console's light or dark theme, keeps a skeleton up until the dashboard
 * has actually loaded, and says so plainly when it cannot.
 *
 * In the offline mock (environment.devMockApi) it shows a placeholder
 * instead: the mock cannot serve Metabase's iframe.
 */
@Component({
  selector: 'metabase-embed',
  standalone: true,
  imports: [],
  templateUrl: './metabase-embed.component.html',
  styleUrl: './metabase-embed.component.css',
})
export class MetabaseEmbedComponent implements OnInit, DoCheck, OnDestroy {
  private metabaseService = inject(MetabaseService);
  private themeService = inject(ThemeService);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  userService = inject(UserProfileService);
  private subscription?: Subscription;
  private slowTimer?: ReturnType<typeof setTimeout>;
  private appliedTheme: string | null = null;

  get organizationId() {
    return this.userService.organizationId;
  }

  // Inputs with transform
  dashboardId = input(environment.metabase.dashboardId, {
    transform: (value: number | null | undefined): number => {
      const parsed = Number(value);
      if (isNaN(parsed) || parsed <= 0) {
        return environment.metabase.dashboardId;
      }
      return parsed;
    },
  });

  // Signal for selected dashboard
  selectedDashboardId = signal<number>(environment.metabase.dashboardId);

  initialParams = input({} as DashboardParams, {
    transform: (value: DashboardParams | null | undefined): DashboardParams => {
      if (!value || typeof value !== 'object') return {};
      return value;
    },
  });

  embeddingParams = input(
    {
      organization_id: 'locked', // Metabase uses American spelling
    } as EmbeddingParams,
    {
      transform: (
        value: EmbeddingParams | null | undefined
      ): EmbeddingParams => {
        if (!value || typeof value !== 'object') {
          return {
            organization_id: 'locked', // Metabase uses American spelling
          };
        }
        return value;
      },
    }
  );

  /** Height of the embed inside its parent. */
  height = input('100%');

  // Optional per-instance overrides — when set, the embed uses a different
  // Metabase host + signing key than the global environment.metabase config.
  instanceUrl = input<string | undefined>(undefined);
  secretKey = input<string | undefined>(undefined);

  // When set, takes precedence over UserProfileService.organizationId.
  orgIdOverride = input<string | undefined>(undefined);

  /** Accessible name of the frame, e.g. "Delivery dashboard". */
  frameTitle = input('Reporting dashboard');

  /** The offline mock cannot serve Metabase's iframe. */
  readonly offlineMock = !!(environment as { devMockApi?: boolean }).devMockApi;

  // Signals
  iframeUrl = signal<SafeResourceUrl | null>(null);
  /** Signing the token, or waiting for the dashboard to load in the frame. */
  loading = signal<boolean>(true);
  error = signal<string | null>(null);
  /** The frame has taken longer than SLOW_MS so far. */
  slow = signal<boolean>(false);
  organisationId = signal<string>('');

  get canFullscreen(): boolean {
    return typeof document !== 'undefined' && !!document.fullscreenEnabled;
  }

  ngOnInit() {
    if (this.offlineMock || !this.isBrowser) return;
    try {
      // Initialize selected dashboard ID
      this.selectedDashboardId.set(this.dashboardId());

      // Set organisation ID from logged-in user (override input wins if provided)
      const userOrgId = this.orgIdOverride() ?? this.organizationId;
      if (userOrgId) {
        this.organisationId.set(userOrgId);
      }

      this.appliedTheme = this.themeService.currentTheme;
      this.loadDashboard();
    } catch (error) {
      this.fail(PREPARE_FAILED);
    }
  }

  /** Reload in the other theme when the console switches between light and dark. */
  ngDoCheck() {
    if (this.offlineMock || !this.isBrowser || this.appliedTheme === null) return;
    const current = this.themeService.currentTheme;
    if (current === this.appliedTheme) return;
    this.appliedTheme = current;
    this.loadDashboard();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.clearSlowTimer();
  }

  private loadDashboard() {
    try {
      this.loading.set(true);
      this.error.set(null);
      this.slow.set(false);
      this.iframeUrl.set(null);
      this.clearSlowTimer();

      if (this.subscription) {
        this.subscription.unsubscribe();
      }

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        this.fail('You are offline. Reconnect, then try again.');
        return;
      }

      const params: DashboardParams = { ...this.initialParams() };
      const orgId = this.organisationId();
      const currentDashboardId = this.selectedDashboardId();

      if (currentDashboardId === environment.metabase.ussdDashboardId) {
        // USSD: seed date range so charts load immediately on first render
        const today = new Date();
        const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (!params['end_date']) params['end_date'] = today.toISOString().split('T')[0];
        if (!params['start_date']) params['start_date'] = thirtyDaysAgo.toISOString().split('T')[0];
      }

      if (orgId?.trim()) {
        if (currentDashboardId === environment.metabase.ussdDashboardId) {
          // USSD dashboard: organisation_id stored as MongoDB Binary — pass as base64
          params['organisation_id'] = this.metabaseService.uuidToBase64(orgId.trim());
        } else {
          // SMS dashboard: organization_id is a plain UUID string
          params['organization_id'] = orgId.trim();
        }
      }

      // Resolve embedding params: lock org_id only when a value is available
      let resolvedEmbeddingParams: EmbeddingParams;
      if (currentDashboardId === environment.metabase.ussdDashboardId) {
        resolvedEmbeddingParams = {
          organisation_id: orgId?.trim() ? 'locked' : 'disabled',
          start_date: 'enabled',
          end_date: 'enabled',
        };
      } else {
        resolvedEmbeddingParams = {
          organization_id: orgId?.trim() ? 'locked' : 'disabled',
          ...this.embeddingParams()
        };
      }

      const overrideInstance = this.instanceUrl();
      const overrideSecret = this.secretKey();
      const override = overrideInstance || overrideSecret
        ? { instanceUrl: overrideInstance, secretKey: overrideSecret }
        : undefined;

      // The console header owns the title and the panel owns the frame.
      const display = {
        bordered: false,
        titled: false,
        theme: this.themeService.currentTheme === 'dark' ? ('night' as const) : null,
      };

      this.subscription = this.metabaseService
        .getSafeEmbedUrl(currentDashboardId, params, resolvedEmbeddingParams, override, display)
        .subscribe({
          next: (url: SafeResourceUrl) => {
            // Stay in the loading state until the frame reports it has loaded.
            this.iframeUrl.set(url);
            this.slowTimer = setTimeout(() => {
              if (this.loading()) this.slow.set(true);
            }, SLOW_MS);
          },
          error: () => {
            this.fail(LINK_FAILED);
          },
        });
    } catch (error) {
      this.fail(PREPARE_FAILED);
    }
  }

  /**
   * The frame finished loading: show it. An iframe inserted before its src is
   * bound fires a load for about:blank first; that one is ignored, so the
   * skeleton (and the slow-load timer) stay until Metabase itself has loaded.
   */
  onFrameLoad(event: Event) {
    if (!this.iframeUrl()) return;
    const frame = event.target as HTMLIFrameElement | null;
    if (!frame?.getAttribute('src') || this.showsBlankPage(frame)) return;
    this.clearSlowTimer();
    this.slow.set(false);
    this.loading.set(false);
  }

  retry() {
    this.loadDashboard();
  }

  refresh() {
    this.loadDashboard();
  }

  /** Show the dashboard on the whole screen (Esc returns). */
  enterFullscreen() {
    if (!this.canFullscreen) return;
    this.host.nativeElement.requestFullscreen?.().catch(() => {});
  }

  /** True while the frame still holds its initial about:blank document. */
  private showsBlankPage(frame: HTMLIFrameElement): boolean {
    try {
      return frame.contentWindow?.location.href === 'about:blank';
    } catch {
      // Cross-origin: Metabase's page is in the frame.
      return false;
    }
  }

  private fail(message: string) {
    this.clearSlowTimer();
    this.iframeUrl.set(null);
    this.slow.set(false);
    this.error.set(message);
    this.loading.set(false);
  }

  private clearSlowTimer() {
    if (this.slowTimer) {
      clearTimeout(this.slowTimer);
      this.slowTimer = undefined;
    }
  }
}
